import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Users, Globe, Search } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PublishExamDialog = ({ open, onOpenChange, exam, onSuccess }) => {
  const { t } = useTranslation();
  const [publishType, setPublishType] = useState('all');
  const [students, setStudents] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [loadingStudents, setLoadingStudents] = useState(false);

  useEffect(() => {
    if (open && exam) {
      // Set initial values based on exam
      setPublishType(exam.publish_type || 'all');
      setSelectedStudents(exam.allowed_students || []);
      
      // Fetch students list
      fetchStudents();
    }
  }, [open, exam]);

  const fetchStudents = async () => {
    setLoadingStudents(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/teacher/students`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStudents(response.data.students || []);
    } catch (error) {
      console.error('Failed to fetch students:', error);
      toast.error('فشل تحميل قائمة الطلاب');
    } finally {
      setLoadingStudents(false);
    }
  };

  const handleStudentToggle = (studentId) => {
    setSelectedStudents(prev => {
      if (prev.includes(studentId)) {
        return prev.filter(id => id !== studentId);
      } else {
        return [...prev, studentId];
      }
    });
  };

  const handleSelectAll = () => {
    const filtered = getFilteredStudents();
    const allFilteredIds = filtered.map(s => s.id);
    setSelectedStudents(allFilteredIds);
  };

  const handleDeselectAll = () => {
    setSelectedStudents([]);
  };

  const handlePublish = async (published) => {
    if (published && publishType === 'specific' && selectedStudents.length === 0) {
      toast.error('يرجى اختيار طالب واحد على الأقل');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const publishSettings = {
        published,
        publish_type: publishType,
        allowed_students: publishType === 'specific' ? selectedStudents : []
      };

      await axios.put(
        `${API}/teacher/exams/${exam.id}/publish`,
        publishSettings,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (published) {
        if (publishType === 'all') {
          toast.success('تم نشر الامتحان لجميع الطلاب');
        } else {
          toast.success(`تم نشر الامتحان لـ ${selectedStudents.length} طالب`);
        }
      } else {
        toast.success('تم إلغاء نشر الامتحان');
      }

      onSuccess();
      onOpenChange(false);
    } catch (error) {
      console.error('Publish error:', error);
      toast.error(error.response?.data?.detail || 'فشل النشر');
    } finally {
      setLoading(false);
    }
  };

  const getFilteredStudents = () => {
    if (!searchQuery) return students;
    const query = searchQuery.toLowerCase();
    return students.filter(student => 
      student.name.toLowerCase().includes(query) || 
      student.email.toLowerCase().includes(query)
    );
  };

  const filteredStudents = getFilteredStudents();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl">
            📢 نشر الاختبار: {exam?.title}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Publish Type Selection */}
          <div className="space-y-4">
            <Label className="text-lg font-semibold">اختر طريقة النشر:</Label>
            
            <RadioGroup value={publishType} onValueChange={setPublishType}>
              <div className="flex items-center space-x-2 space-x-reverse p-4 border rounded-lg hover:bg-gray-50 cursor-pointer">
                <RadioGroupItem value="all" id="all" />
                <Label htmlFor="all" className="flex items-center gap-2 cursor-pointer flex-1">
                  <Globe className="h-5 w-5 text-blue-500" />
                  <div>
                    <div className="font-semibold">نشر للجميع</div>
                    <div className="text-sm text-gray-500">جميع الطلاب المسجلين يمكنهم رؤية الامتحان</div>
                  </div>
                </Label>
              </div>

              <div className="flex items-center space-x-2 space-x-reverse p-4 border rounded-lg hover:bg-gray-50 cursor-pointer">
                <RadioGroupItem value="specific" id="specific" />
                <Label htmlFor="specific" className="flex items-center gap-2 cursor-pointer flex-1">
                  <Users className="h-5 w-5 text-green-500" />
                  <div>
                    <div className="font-semibold">نشر لطلاب محددين</div>
                    <div className="text-sm text-gray-500">اختر الطلاب الذين تريد نشر الامتحان لهم</div>
                  </div>
                </Label>
              </div>
            </RadioGroup>
          </div>

          {/* Students Selection (only if specific) */}
          {publishType === 'specific' && (
            <div className="space-y-4 border-t pt-4">
              <div className="flex items-center justify-between">
                <Label className="text-lg font-semibold">
                  اختر الطلاب ({selectedStudents.length} محدد)
                </Label>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleSelectAll}
                    disabled={filteredStudents.length === 0}
                  >
                    تحديد الكل
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleDeselectAll}
                    disabled={selectedStudents.length === 0}
                  >
                    إلغاء التحديد
                  </Button>
                </div>
              </div>

              {/* Search */}
              <div className="relative">
                <Search className="absolute right-3 top-3 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="ابحث بالاسم أو البريد الإلكتروني..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pr-10"
                />
              </div>

              {/* Students List */}
              {loadingStudents ? (
                <div className="text-center py-8 text-gray-500">
                  جاري تحميل الطلاب...
                </div>
              ) : filteredStudents.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  {searchQuery ? 'لا توجد نتائج' : 'لا يوجد طلاب مسجلين'}
                </div>
              ) : (
                <div className="border rounded-lg max-h-64 overflow-y-auto">
                  {filteredStudents.map((student) => (
                    <div
                      key={student.id}
                      className="flex items-center space-x-3 space-x-reverse p-3 hover:bg-gray-50 border-b last:border-b-0 cursor-pointer"
                      onClick={() => handleStudentToggle(student.id)}
                    >
                      <Checkbox
                        checked={selectedStudents.includes(student.id)}
                        onCheckedChange={() => handleStudentToggle(student.id)}
                      />
                      <div className="flex-1">
                        <div className="font-medium">{student.name}</div>
                        <div className="text-sm text-gray-500">{student.email}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Summary */}
          <div className="bg-blue-50 p-4 rounded-lg">
            <div className="font-semibold text-blue-900 mb-2">📊 ملخص النشر:</div>
            {publishType === 'all' ? (
              <div className="text-blue-800">
                • سيتم نشر الامتحان لجميع الطلاب ({students.length} طالب)
              </div>
            ) : (
              <div className="text-blue-800">
                • سيتم نشر الامتحان لـ <span className="font-bold">{selectedStudents.length}</span> طالب محدد
                {selectedStudents.length > 0 && (
                  <div className="mt-1 text-sm">• سيتم إرسال إشعارات للطلاب المحددين</div>
                )}
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={loading}
          >
            إلغاء
          </Button>
          
          {exam?.published && (
            <Button
              type="button"
              variant="destructive"
              onClick={() => handlePublish(false)}
              disabled={loading}
            >
              {loading ? 'جاري الإلغاء...' : 'إلغاء النشر'}
            </Button>
          )}
          
          <Button
            type="button"
            onClick={() => handlePublish(true)}
            disabled={loading || (publishType === 'specific' && selectedStudents.length === 0)}
            className="bg-green-500 hover:bg-green-600"
          >
            {loading ? 'جاري النشر...' : exam?.published ? 'تحديث النشر' : 'نشر الآن'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PublishExamDialog;
